/**
 * FullDiagnosticsPanel — Kompleksowa diagnostyka KSeF (4 stages, jeden button).
 *
 * Refaktor 2026-06-01: zastępuje 3 osobne panele (CertKeyPairTest + HealthCheck + MtlsTest).
 *
 * Wykonuje sekwencyjnie:
 *   Stage 1: Konfiguracja lokalna (env + pliki + hasło)
 *   Stage 2: Walidacja pary (klucz odblokowuje się hasłem)
 *   Stage 3: mTLS handshake (TCP + TLS + HTTP do KSeF API)
 *   Stage 4: Pełen XAdES auth flow (challenge → xades → polling → redeem)
 *
 * Wynik kopiowany do schowka (markdown) lub pobierany jako JSON.
 */
import { useState } from "react";
import { logger } from "../../../utils/logger";
import { toast } from "sonner";
import {
  Activity,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronRight,
  Clipboard,
  Download,
  Play,
  Clock,
  Terminal,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { runFullDiagnostics } from "../services/ksefApi";

const formatDuration = (ms) => {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

const buildMarkdownReport = (report) => {
  const lines = [];
  lines.push(`# Diagnostyka KSeF — Raport`);
  lines.push("");
  lines.push(`**Correlation ID:** \`${report.correlation_id}\``);
  lines.push(`**Wykonano:** ${report.started_at} → ${report.finished_at}`);
  lines.push(`**Czas całkowity:** ${formatDuration(report.total_duration_ms)}`);
  lines.push(`**Wynik ogólny:** ${report.overall_ok ? "✅ OK" : "❌ FAIL"}`);
  lines.push("");

  if (report.summary?.recommendation) {
    lines.push(`## Zalecenie`);
    lines.push(`> ${report.summary.recommendation}`);
    lines.push("");
  }

  for (const stage of report.stages) {
    lines.push(`## ${stage.title} ${stage.ok ? "✅" : "❌"} (${formatDuration(stage.duration_ms)})`);
    for (const check of stage.checks || []) {
      const icon = check.ok ? "✓" : "✗";
      const valuePart = check.value !== null && check.value !== undefined ? ` — \`${check.value}\`` : "";
      const notePart = check.note ? ` _(${check.note})_` : "";
      lines.push(`- ${icon} **${check.label}**${valuePart}${notePart}`);
    }
    if (stage.error) {
      lines.push(`- ⚠️ **Error:** ${stage.error}`);
    }
    if (stage.recommendation) {
      lines.push(`- 💡 **Zalecenie:** ${stage.recommendation}`);
    }
    lines.push("");
  }

  if (report.backend_logs && report.backend_logs.length > 0) {
    lines.push(`## Logi backendu (${report.backend_logs.length}${report.backend_logs_truncated ? ", obcięte" : ""})`);
    lines.push("```");
    for (const line of report.backend_logs) {
      lines.push(line);
    }
    lines.push("```");
    lines.push("");
  }

  return lines.join("\n");
};

const downloadJson = (report) => {
  const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ksef-diagnostics-${report.correlation_id}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const StageCard = ({ stage, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div
      className={`border rounded-lg overflow-hidden ${
        stage.ok ? "border-green-200 bg-green-50/30" : "border-red-200 bg-red-50/30"
      }`}
      data-testid={`stage-card-${stage.name}`}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 hover:bg-black/5 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          {stage.ok ? (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          ) : (
            <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          )}
          <span className="font-medium text-sm text-gray-900 truncate text-left">
            {stage.title}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 text-xs text-gray-500">
          <Clock className="w-3 h-3" />
          {formatDuration(stage.duration_ms)}
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-3 space-y-1.5 text-sm border-t border-current/10">
          {(stage.checks || []).map((check, idx) => (
            <div key={idx} className="flex items-start gap-2 pt-2">
              {check.ok ? (
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-medium text-gray-700">{check.label}</span>
                  {check.value !== null && check.value !== undefined && (
                    <span className="font-mono text-xs text-gray-600 bg-white px-1.5 py-0.5 rounded border break-all">
                      {String(check.value)}
                    </span>
                  )}
                </div>
                {check.note && (
                  <p className="text-xs text-gray-500 mt-0.5 break-words">{check.note}</p>
                )}
              </div>
            </div>
          ))}

          {stage.error && (
            <div className="bg-red-100 border border-red-200 rounded p-2 mt-2 text-xs text-red-900 font-mono break-all">
              ⚠️ {stage.error}
            </div>
          )}

          {stage.recommendation && (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 mt-2 text-xs text-amber-900">
              💡 <strong>Zalecenie:</strong> {stage.recommendation}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const BackendLogsCard = ({ logs, truncated, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      className="border rounded-lg overflow-hidden border-gray-200 bg-gray-50"
      data-testid="backend-logs-card"
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 hover:bg-black/5 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Terminal className="w-5 h-5 text-gray-500 flex-shrink-0" />
          <span className="font-medium text-sm text-gray-900 truncate text-left">
            Logi backendu ({logs.length}{truncated ? ", obcięte" : ""})
          </span>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
      </button>

      {open && (
        <div className="border-t border-gray-200 bg-gray-900 text-gray-100 max-h-96 overflow-auto">
          <pre
            className="text-[11px] leading-relaxed font-mono px-4 py-3 whitespace-pre-wrap break-all"
            data-testid="backend-logs-pre"
          >
            {logs.join("\n")}
          </pre>
        </div>
      )}
    </div>
  );
};

export const FullDiagnosticsPanel = ({ onTested }) => {
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);

  const handleRun = async () => {
    setRunning(true);
    setReport(null);
    try {
      const response = await runFullDiagnostics();
      setReport(response.data);
      if (onTested) onTested();
    } catch (err) {
      logger.error("Diagnostics error:", err);
      toast.error(err.response?.data?.detail || err.message || "Błąd diagnostyki");
    } finally {
      setRunning(false);
    }
  };

  const handleCopyMarkdown = async () => {
    if (!report) return;
    try {
      await navigator.clipboard.writeText(buildMarkdownReport(report));
      toast.success("Raport (markdown) skopiowany do schowka");
    } catch (err) {
      logger.error("Clipboard error:", err);
      toast.error("Nie udało się skopiować raportu");
    }
  };

  return (
    <div className="space-y-4" data-testid="full-diagnostics-panel">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Activity className="w-4 h-4 text-gray-500" />
          <h4 className="text-sm font-semibold text-gray-700">Pełna diagnostyka KSeF</h4>
        </div>
        <p className="text-xs text-gray-500">
          Wykonuje 5 stages (każdy niezależnie): konfiguracja lokalna, walidacja pary, mTLS handshake,
          probe /auth/challenge, pełen orchestrator E2E. Sekrety w raporcie są maskowane.
        </p>
      </div>

      <Button
        onClick={handleRun}
        disabled={running}
        className="w-full"
        data-testid="run-full-diagnostics-btn"
      >
        {running ? (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
        ) : (
          <Play className="w-4 h-4 mr-2" />
        )}
        {running ? "Diagnostyka w toku..." : "Uruchom pełną diagnostykę"}
      </Button>

      {report && (
        <div className="space-y-3" data-testid="diagnostics-report">
          {/* Summary */}
          <div
            className={`rounded-lg p-3 border ${
              report.overall_ok
                ? "bg-green-50 border-green-200 text-green-900"
                : "bg-red-50 border-red-200 text-red-900"
            }`}
          >
            <div className="flex items-start gap-2">
              {report.overall_ok ? (
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
              ) : (
                <XCircle className="w-5 h-5 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">
                  {report.summary?.status === "OK"
                    ? "KSeF działa end-to-end"
                    : report.summary?.stages_failed
                      ? `${report.summary.stages_failed} z ${report.summary.stages_total || report.stages.length} stages zakończone błędem`
                      : `Problem: ${report.summary?.failed_stage_title || "Nieznany"}`}
                </p>
                {report.summary?.message && (
                  <p className="text-xs mt-0.5">{report.summary.message}</p>
                )}
                {report.summary?.recommendation && (
                  <p className="text-xs mt-1.5 bg-white/60 rounded px-2 py-1">
                    💡 {report.summary.recommendation}
                  </p>
                )}
                <div className="text-xs mt-2 opacity-75 flex gap-3 flex-wrap font-mono">
                  <span>ID: {report.correlation_id}</span>
                  <span>Czas: {formatDuration(report.total_duration_ms)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Stages */}
          <div className="space-y-2">
            {report.stages.map((stage) => (
              <StageCard key={stage.name} stage={stage} defaultOpen={!stage.ok} />
            ))}
          </div>

          {/* Backend logs */}
          {Array.isArray(report.backend_logs) && report.backend_logs.length > 0 && (
            <BackendLogsCard
              logs={report.backend_logs}
              truncated={!!report.backend_logs_truncated}
              defaultOpen={!report.overall_ok}
            />
          )}

          {/* Export buttons */}
          <div className="flex gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyMarkdown}
              className="flex-1"
              data-testid="copy-report-btn"
            >
              <Clipboard className="w-3.5 h-3.5 mr-1.5" />
              Kopiuj raport (markdown)
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadJson(report)}
              className="flex-1"
              data-testid="download-report-btn"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Pobierz JSON
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FullDiagnosticsPanel;
